import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { client } from "../api";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Checkbox,
  Autocomplete,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormControl,
  Grid,
  CircularProgress,
  LinearProgress,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Radio,
  RadioGroup,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

const DASHBOARD_SELECTED_TENANT_KEY = "admin_dashboard_selected_tenant_id";

/**
 * Coerce an API error value into a displayable string. FastAPI/Pydantic 422s return
 * `detail` as an array of objects; rendering that directly as a React child crashes the
 * page ("Objects are not valid as a React child"), so always normalize before display.
 */
function formatApiError(value, fallback = "Something went wrong") {
  if (typeof value === "string") return value.trim() || fallback;
  if (Array.isArray(value)) {
    const msg = value
      .map((e) => (typeof e === "string" ? e : e?.msg))
      .filter(Boolean)
      .join("; ");
    return msg || fallback;
  }
  if (value && typeof value === "object") {
    return value.msg || value.detail || fallback;
  }
  return fallback;
}

/** Two catalog stacks. Shopify tenants are created by the app install (the
 * plugin sends the feed URL / token over the API), so this page only repairs
 * them. Magento tenants are configured here: the catalog comes either from a
 * product feed XML URL or from a remote MySQL DSN — the URL decides which.
 * Mode values must match the backend's resolve_source_plan. */
const SOURCE_DB_TYPES = [
  { value: "shopify", label: "Shopify" },
  { value: "magento", label: "Magento" },
];

const SOURCE_MODE_BY_DB_TYPE = {
  shopify: [
    { value: "shopify", label: "Shopify catalog only" },
    { value: "mixed", label: "Shopify + Static URLs" },
  ],
  magento: [
    { value: "magento", label: "Magento catalog only" },
    { value: "mixed", label: "Magento + Static URLs" },
  ],
};

const isMagentoFeedUrl = (value) => /^https?:\/\//i.test((value || "").trim());

/** What the source URL field means for each stack. */
const SOURCE_URL_FIELD = {
  shopify: {
    label: "Product feed URL (optional)",
    placeholder: "https://cdn.store.com/products.json",
    help: "Sent by the Shopify plugin at install. Leave empty to read the catalog from the Shopify Admin API using the token captured at install; set it only if the store publishes a hosted JSON snapshot instead.",
  },
  magento: {
    label: "Magento source: product feed XML URL or MySQL DSN",
    placeholder: "https://store.com/media/feed/feed.xml  or  mysql://user:password@host:3306/database",
    help: "Two seeding options. Feed XML: an https:// URL to the store's product feed (no database access needed). Remote DB: a mysql:// DSN with read access to the Magento database (also indexes CMS pages). URL-encode special characters in the password.",
  },
};

const normalizeSourceModeForDbType = (dbType, mode) => {
  const options = SOURCE_MODE_BY_DB_TYPE[dbType] || SOURCE_MODE_BY_DB_TYPE.shopify;
  const allowed = options.map((o) => o.value);
  const raw = (mode || "").trim().toLowerCase();
  if (allowed.includes(raw)) return raw;
  return allowed[0];
};

/* Shopify is read over HTTP: no database, no table prefix, no URL table.
   Magento keeps its store-view id in the URL-table column (default 1) and an
   optional table prefix for non-standard installs. */
const applySourceDefaultsForDbType = (dbType, { tablePrefix, urlTable }) => {
  if (dbType === "magento") {
    const prefix = tablePrefix ?? "";
    const store = urlTable ?? "";
    return {
      tablePrefix: prefix === "wp_" || prefix === "wp" ? "" : prefix,
      urlTable: store === "" || store === "wp_custom_urls" ? "1" : store,
    };
  }
  return { tablePrefix: tablePrefix ?? "", urlTable: urlTable ?? "" };
};

const adminApiBase = () => String(client.defaults.baseURL || "").replace(/\/+$/, "");

/** Normalized reindex progress from API (planned total vs processed). */
const reindexJobProgress = (job) => {
  const p = job?.meta?.progress;
  if (!p) return null;
  const total = Number(p.total_items ?? p.planned_total_records ?? 0);
  const processed = Number(p.processed_items ?? 0);
  const pct = Number(
    p.progress_percentage ?? (total > 0 ? Math.min(100, (processed / total) * 100) : 0),
  );
  return {
    total,
    processed,
    remaining: Number(p.remaining_items ?? Math.max(0, total - processed)),
    failed: Number(p.failed_items ?? 0),
    percentage: pct,
  };
};

const AdminDashboard = ({ role, userEmail, onLogout, tenantId, tenantIds = [] }) => {
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [chatsPage, setChatsPage] = useState(1);
  const [chatsTotal, setChatsTotal] = useState(0);
  const [adminsPage, setAdminsPage] = useState(1);
  const [adminsTotal, setAdminsTotal] = useState(0);
  const [tenants, setTenants] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminTenantId, setAdminTenantId] = useState("");
  const [adminTenantMode, setAdminTenantMode] = useState("existing");
  const [newUserRole, setNewUserRole] = useState(role === "admin" ? "manager" : "admin");
  const [newTenantName, setNewTenantName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [chatFilter, setChatFilter] = useState("");
  const [sourceTenantId, setSourceTenantId] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(DASHBOARD_SELECTED_TENANT_KEY) || "" : "",
  );
  const [sourceDbUrl, setSourceDbUrl] = useState("");
  const [sourceDbType, setSourceDbType] = useState("shopify");
  const [sourceTablePrefix, setSourceTablePrefix] = useState("");
  const [sourceUrlTable, setSourceUrlTable] = useState("wp_custom_urls");
  const [sourceMode, setSourceMode] = useState("shopify");
  const [sourceStaticUrlsJson, setSourceStaticUrlsJson] = useState("");
  const [sourceDomainAliases, setSourceDomainAliases] = useState("");
  const [sourceCanonicalBaseUrl, setSourceCanonicalBaseUrl] = useState("");
  const [brandName, setBrandName] = useState("");
  const [widgetPrimaryColor, setWidgetPrimaryColor] = useState("#bf362e");
  const [corsAllowedOrigins, setCorsAllowedOrigins] = useState("");
  const [monthlyMessageLimit, setMonthlyMessageLimit] = useState(15000);
  const [quotaReachedMessage, setQuotaReachedMessage] = useState("Monthly message limit reached. Please try again next month.");
  const [aiEnabled, setAiEnabled] = useState(true);
  const [poweredByText, setPoweredByText] = useState("Powered by Company Name");
  const [blockedIps, setBlockedIps] = useState([]);
  const [blockedCountries, setBlockedCountries] = useState([]);
  const [newBlockedIp, setNewBlockedIp] = useState("");
  const [newBlockedIpReason, setNewBlockedIpReason] = useState("");
  const [newBlockedCountry, setNewBlockedCountry] = useState("");
  const [newBlockedCountryReason, setNewBlockedCountryReason] = useState("");
  const [countryReference, setCountryReference] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [manageTenantsOpen, setManageTenantsOpen] = useState(false);
  const [manageTenantUser, setManageTenantUser] = useState(null);
  const [manageTenantIds, setManageTenantIds] = useState([]);
  const [manageTenantSaving, setManageTenantSaving] = useState(false);
  const [createTenantOpen, setCreateTenantOpen] = useState(false);
  const [newStandaloneTenantName, setNewStandaloneTenantName] = useState("");
  const [blockWordCategories, setBlockWordCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryMode, setNewCategoryMode] = useState("exact");
  const [newCategoryResponse, setNewCategoryResponse] = useState("");
  const [newWordByCategory, setNewWordByCategory] = useState({});
  const [productAssistantPanel, setProductAssistantPanel] = useState(0);
  const [productWidgetEnabled, setProductWidgetEnabled] = useState(false);
  const [productWidgetSystemPrompt, setProductWidgetSystemPrompt] = useState("");
  const [productWidgetOfftopicMessage, setProductWidgetOfftopicMessage] = useState("");
  const [productQuestionsFeedUrl, setProductQuestionsFeedUrl] = useState("");
  const [productWidgetCustomCss, setProductWidgetCustomCss] = useState("");
  const [questionsFeedSyncBusy, setQuestionsFeedSyncBusy] = useState(false);
  const [productQuickQuestions, setProductQuickQuestions] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [newPqCategories, setNewPqCategories] = useState([]); // [] = all products
  const [newPqQuestionText, setNewPqQuestionText] = useState("");
  const [newPqPriority, setNewPqPriority] = useState("0");
  const [productQuickQuestionDrafts, setProductQuickQuestionDrafts] = useState({});
  const [productOverrides, setProductOverrides] = useState([]);
  const [newPoProductKey, setNewPoProductKey] = useState("");
  const [newPoProductLabel, setNewPoProductLabel] = useState("");
  const [newPoExtraContext, setNewPoExtraContext] = useState("");
  const [productOverrideDrafts, setProductOverrideDrafts] = useState({});
  const [retrievalProfile, setRetrievalProfile] = useState(null);
  const [retrievalProfileLoading, setRetrievalProfileLoading] = useState(false);
  const [expandedFacetAliases, setExpandedFacetAliases] = useState({});
  const [profileJsonDraft, setProfileJsonDraft] = useState("");
  const [profileJsonOpen, setProfileJsonOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [gazetteerDrafts, setGazetteerDrafts] = useState({});
  const [success, setSuccess] = useState("");
  // Global section-fetch indicator (top progress bar) + conversation-panel spinner.
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const sessionRequestRef = useRef(0);
  const countriesLoadedRef = useRef(false);
  const jobsPollFailsRef = useRef(0);
  // Smoothed per-job discovery percentage: the fetcher reports one tick per
  // 100-product batch (~11% jumps on an 883-product catalog); this creeps the
  // displayed bar through the in-between percents so it counts 1,2,3…100
  // instead of jumping 11→23→34.
  const [smoothDiscoveryPct, setSmoothDiscoveryPct] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const canSelectTenant = role === "superadmin" || tenantIds.length > 1 || tenants.length > 1;
  const effectiveTenantId = sourceTenantId || tenantId || undefined;
  const PAGE_SIZE = 10;
  const routeTail = (location.pathname.replace("/admin/dashboard/", "") || "overview").replace(/^\/+/, "");
  const routeParts = routeTail.split("/").filter(Boolean);
  const section = routeParts[0] || "overview";
  const usersSubsection = section === "users" ? (routeParts[1] || "admins") : "admins";
  const settingsSubsection = section === "settings" ? (routeParts[1] || "general") : "general";

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === sourceTenantId),
    [tenants, sourceTenantId],
  );
  const [embedCopied, setEmbedCopied] = useState(false);
  const [keyBusy, setKeyBusy] = useState(false);
  const embedConfig = useMemo(() => {
    const origin =
      typeof window !== "undefined"
        ? `${window.location.protocol}//${window.location.host}`
        : "";
    const runtime = (typeof window !== "undefined" && window.RUNTIME_CONFIG) || {};
    const baseUrl = runtime.WIDGET_BASE_URL || origin;
    const apiUrl = runtime.API_BASE_URL || process.env.REACT_APP_API_URL || origin;
    const tenantPublicKey = selectedTenant?.widget_public_key || "";
    const snippet = `<script>
  window.CHATBOT_PRODUCT_CONFIG = {
    baseUrl: '${baseUrl}',
    apiUrl: '${apiUrl}',
    tenantPublicKey: '${tenantPublicKey || "replace-with-widget-key"}',

    // Where the assistant renders on product pages (CSS selector).
    // '.product__info-wrapper' matches Dawn and most themes based on it.
    mountSelector: '.product__info-wrapper',
    insertPosition: 'append', // append | prepend | before | after

    // Shopify themes: fill these from Liquid so the assistant knows the
    // product without relying on JSON-LD markup being present.
    // entityId: '{{ product.id }}', productUrl: '{{ shop.url }}{{ product.url }}'
  };
</script>
<script src="${baseUrl}/product-widget.js" async></script>`;
    return { baseUrl, apiUrl, tenantPublicKey, snippet };
  }, [selectedTenant]);
  const copyEmbedSnippet = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(embedConfig.snippet);
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
    } catch {
      /* clipboard unavailable; user can still select the text manually */
    }
  }, [embedConfig.snippet]);

  const countryLabelByCode = useMemo(() => {
    const m = {};
    (countryReference || []).forEach((c) => {
      if (c?.code) m[c.code] = c.name;
    });
    return m;
  }, [countryReference]);

  const navigateSection = (nextSection) => {
    navigate(`/admin/dashboard/${nextSection}`);
  };

  const navigateSettingsSubsection = (nextSubsection) => {
    navigate(`/admin/dashboard/settings/${nextSubsection}`);
  };

  const persistSourceTenantId = useCallback((value) => {
    setSourceTenantId(value);
    if (value) {
      localStorage.setItem(DASHBOARD_SELECTED_TENANT_KEY, value);
    } else {
      localStorage.removeItem(DASHBOARD_SELECTED_TENANT_KEY);
    }
  }, []);

  const refreshJobsOnly = useCallback(async () => {
    try {
      const { data } = await client.get("/api/reindex/jobs", { params: { tenant_id: effectiveTenantId } });
      setJobs(data);
      jobsPollFailsRef.current = 0;
      // A previously shown poll error is stale once polling recovers.
      setError((prev) => (prev === "Failed loading jobs" ? "" : prev));
    } catch (err) {
      // One blip (backend restart, laptop wake) shouldn't paint a sticky
      // red banner over a working page — only surface a sustained outage.
      jobsPollFailsRef.current += 1;
      if (jobsPollFailsRef.current >= 3) {
        setError("Failed loading jobs");
      }
    }
  }, [effectiveTenantId]);

  useEffect(() => {
    const timer = setInterval(() => {
      setSmoothDiscoveryPct((prev) => {
        const next = {};
        let changed = false;
        (jobs || []).forEach((job) => {
          if (job.status !== "running") return;
          const p = job?.meta?.progress || {};
          const total = Number(p.discovery_total || 0);
          const done = Number(p.discovery_done || 0);
          if (!(total > 0) || done >= total) return;
          const actual = (done / total) * 100;
          // One fetch batch expressed in % — creep covers it in ~100s,
          // matching how long a batch takes on a slow store database.
          const batchSpan = Math.min((100 / total) * 100, 15);
          const cap = Math.min(actual + batchSpan, 99);
          const current = prev[job.id] ?? actual;
          const value = current < actual ? actual : Math.min(current + batchSpan / 100, cap);
          next[job.id] = value;
          if (value !== prev[job.id]) changed = true;
        });
        if (!changed && Object.keys(next).length === Object.keys(prev).length) return prev;
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [jobs]);

  const loadSecuritySettings = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/security`);
      setBlockedIps(data.blocked_ips || []);
      setBlockedCountries(data.blocked_countries || []);
      setMonthlyMessageLimit(Number(data.monthly_message_limit || 15000));
      setQuotaReachedMessage(data.quota_reached_message || "Monthly message limit reached. Please try again next month.");
      setCorsAllowedOrigins(data.cors_allowed_origins || "");
      setAiEnabled(data.ai_enabled !== false);
      setPoweredByText(data.widget_powered_by_text ?? "Powered by Company Name");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading security settings");
    }
  }, [effectiveTenantId]);

  const loadBlockWordSettings = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/block-word-categories`);
      setBlockWordCategories(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading block-word settings");
    }
  }, [effectiveTenantId]);

  const loadProductCategories = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/product-categories`);
      setProductCategories(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading product categories");
    }
  }, [effectiveTenantId]);

  const loadProductQuickQuestions = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/product-quick-questions`);
      setProductQuickQuestions(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading product quick questions");
    }
  }, [effectiveTenantId]);

  const loadProductOverrides = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/product-overrides`);
      setProductOverrides(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading product overrides");
    }
  }, [effectiveTenantId]);

  const loadRetrievalProfile = useCallback(async () => {
    if (!effectiveTenantId) return;
    setRetrievalProfileLoading(true);
    try {
      const { data } = await client.get(`/api/admin/tenants/${effectiveTenantId}/retrieval-profile`);
      setRetrievalProfile(data);
      setError("");
    } catch (err) {
      setRetrievalProfile(null);
      setError(err?.response?.data?.detail || "Failed loading retrieval profile");
    } finally {
      setRetrievalProfileLoading(false);
    }
  }, [effectiveTenantId]);

  const openProfileJsonEditor = useCallback(() => {
    const profile = retrievalProfile?.profile;
    setProfileJsonDraft(profile ? JSON.stringify(profile, null, 2) : "{}");
    setProfileJsonOpen(true);
  }, [retrievalProfile]);

  const saveProfileJson = useCallback(async () => {
    if (!effectiveTenantId) return;
    setProfileSaving(true);
    try {
      const parsed = JSON.parse(profileJsonDraft);
      const { data } = await client.put(`/api/admin/tenants/${effectiveTenantId}/retrieval-profile`, {
        profile: parsed,
      });
      setRetrievalProfile(data);
      setProfileJsonOpen(false);
      setSuccess("Retrieval profile updated.");
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to save retrieval profile");
    } finally {
      setProfileSaving(false);
    }
  }, [effectiveTenantId, profileJsonDraft]);

  const saveGazetteerFlags = useCallback(async () => {
    if (!effectiveTenantId || !retrievalProfile?.profile) return;
    const entries = Object.entries(gazetteerDrafts).map(([id, draft]) => ({
      id,
      hard_filter: draft.hard_filter ?? null,
      demote_accessory_substrings: draft.demote_accessory_substrings ?? null,
      fixture_stem: draft.fixture_stem ?? null,
      accessory_keywords: draft.accessory_keywords ?? null,
    }));
    if (!entries.length) return;
    setProfileSaving(true);
    try {
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/retrieval-profile/gazetteer`, { entries });
      await loadRetrievalProfile();
      setGazetteerDrafts({});
      setSuccess("Gazetteer match flags updated.");
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update gazetteer flags");
    } finally {
      setProfileSaving(false);
    }
  }, [effectiveTenantId, gazetteerDrafts, loadRetrievalProfile, retrievalProfile]);

  const loadTenants = useCallback(async () => {
    try {
      const { data } = await client.get("/api/admin/tenants");
      setTenants(data || []);
      if (!sourceTenantId && data?.length > 0) {
        const firstTenant = tenantIds[0] || tenantId || data[0].id;
        const next = firstTenant || "";
        persistSourceTenantId(next);
      }
      if (!adminTenantId && data?.length > 0) {
        const firstTenant = tenantIds[0] || tenantId || data[0].id;
        setAdminTenantId(firstTenant || "");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading tenants");
    }
  }, [adminTenantId, sourceTenantId, tenantId, tenantIds, persistSourceTenantId]);

  const regenerateWidgetKey = useCallback(async () => {
    if (!effectiveTenantId) return;
    const hasKey = !!selectedTenant?.widget_public_key;
    if (hasKey && !window.confirm("Regenerate the widget key? The current key will stop working and any site using it must be updated.")) {
      return;
    }
    setKeyBusy(true);
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/widget-key`);
      await loadTenants();
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed generating widget key");
    } finally {
      setKeyBusy(false);
    }
  }, [effectiveTenantId, selectedTenant, loadTenants]);

  const loadOverviewSection = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get("/api/admin/overview", { params: { tenant_id: effectiveTenantId } });
      setOverview(data);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading overview");
    }
  }, [effectiveTenantId]);

  const loadChatsSection = useCallback(async (query = chatFilter, page = 1) => {
    if (!effectiveTenantId) return;
    try {
      const [chatRes, usageRes] = await Promise.all([
        client.get("/api/admin/chats", {
          params: { q: query || undefined, tenant_id: effectiveTenantId, page, page_size: PAGE_SIZE },
        }),
        client.get("/api/admin/usage/summary", { params: { tenant_id: effectiveTenantId } }),
      ]);
      setSessions(chatRes.data?.items || []);
      setChatsTotal(Number(chatRes.data?.total || 0));
      setChatsPage(Number(chatRes.data?.page || page));
      setUsageSummary(usageRes.data);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading chats");
    }
  }, [effectiveTenantId, chatFilter]);

  const loadJobsSection = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get("/api/reindex/jobs", { params: { tenant_id: effectiveTenantId } });
      setJobs(data || []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading jobs");
    }
  }, [effectiveTenantId]);

  const loadAdminsSection = useCallback(async (page = 1) => {
    if (!effectiveTenantId) return;
    try {
      const { data } = await client.get("/api/admin/users", {
        params: { tenant_id: effectiveTenantId, page, page_size: PAGE_SIZE },
      });
      setUsers(data?.items || []);
      setAdminsTotal(Number(data?.total || 0));
      setAdminsPage(Number(data?.page || page));
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed loading admins");
    }
  }, [effectiveTenantId]);

  useEffect(() => {
    if (section !== "jobs") return undefined;
    const id = setInterval(() => {
      refreshJobsOnly();
    }, 5000);
    return () => clearInterval(id);
  }, [section, refreshJobsOnly]);

  useEffect(() => {
    if (!tenants.length || !sourceTenantId) return;
    if (tenants.some((t) => t.id === sourceTenantId)) return;
    const fallback =
      tenantIds.find((id) => tenants.some((t) => t.id === id)) || tenants[0]?.id || tenantId || "";
    persistSourceTenantId(fallback);
  }, [tenants, sourceTenantId, tenantIds, tenantId, persistSourceTenantId]);

  useEffect(() => {
    if (role !== "superadmin") return;
    setSelected(null);
    setMessages([]);
  }, [role, sourceTenantId]);

  useEffect(() => {
    const t = tenants.find((tenant) => tenant.id === sourceTenantId);
    if (!t) return;
    setSourceDbUrl(t.source_db_url || "");
    setSourceDbType(t.source_db_type || "shopify");
    const dbType = t.source_db_type || "shopify";
    const prefixDefaults = applySourceDefaultsForDbType(dbType, {
      tablePrefix: t.source_table_prefix != null ? t.source_table_prefix : undefined,
      urlTable: t.source_url_table != null ? t.source_url_table : undefined,
    });
    setSourceTablePrefix(prefixDefaults.tablePrefix);
    setSourceUrlTable(prefixDefaults.urlTable);
    setSourceMode(normalizeSourceModeForDbType(t.source_db_type || "shopify", t.source_mode || "shopify"));
    setSourceStaticUrlsJson(t.source_static_urls_json || "");
    setSourceDomainAliases(t.source_domain_aliases || "");
    setSourceCanonicalBaseUrl(t.source_canonical_base_url || "");
    setBrandName(t.brand_name || "");
    setWidgetPrimaryColor(t.widget_primary_color || "");
    setCorsAllowedOrigins(t.cors_allowed_origins || "");
    setProductWidgetEnabled(!!t.product_widget_enabled);
    setProductWidgetSystemPrompt(t.product_widget_system_prompt || "");
    setProductWidgetOfftopicMessage(t.product_widget_offtopic_message || "");
    setProductQuestionsFeedUrl(t.product_questions_feed_url || "");
    setProductWidgetCustomCss(t.product_widget_custom_css || "");
  }, [sourceTenantId, tenants]);

  useEffect(() => {
    setChatsPage(1);
    setAdminsPage(1);
    setSearchQuery("");
    setChatFilter("");
  }, [effectiveTenantId]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    if (section !== "settings" || settingsSubsection !== "security") return;
    if (countriesLoadedRef.current) return;
    countriesLoadedRef.current = true;
    (async () => {
      try {
        const { data } = await client.get("/api/admin/reference/countries");
        setCountryReference(data.countries || []);
      } catch (err) {
        setError(err?.response?.data?.detail || "Failed loading country list");
      }
    })();
  }, [section, settingsSubsection]);

  useEffect(() => {
    // No "search" section: this admin manages the product-page assistant only —
    // the storefront AI-search module from the parent platform is not exposed here.
    const validSections = ["overview", "chats", "jobs", "users", "settings"];
    if (!validSections.includes(section)) {
      navigate("/admin/dashboard/overview", { replace: true });
      return;
    }
    if (section === "users" && !["admins"].includes(usersSubsection)) {
      navigate("/admin/dashboard/users/admins", { replace: true });
      return;
    }
    if (section === "settings" && !["general", "security", "branding", "moderation", "product-assistant", "db-settings", "retrieval-profile"].includes(settingsSubsection)) {
      navigate("/admin/dashboard/settings/general", { replace: true });
      return;
    }
    // Retrieval profile is superadmin-only; bounce admins/managers who reach it by URL.
    if (section === "settings" && settingsSubsection === "retrieval-profile" && role !== "superadmin") {
      navigate("/admin/dashboard/settings/general", { replace: true });
    }
  }, [navigate, section, usersSubsection, settingsSubsection, role]);

  useEffect(() => {
    if (!effectiveTenantId) return;
    // Collect the section's fetches so one progress bar covers them all.
    const tasks = [];
    if (section === "overview") {
      tasks.push(loadOverviewSection());
    } else if (section === "chats") {
      tasks.push(loadChatsSection(chatFilter, chatsPage));
    } else if (section === "jobs") {
      tasks.push(loadJobsSection());
    } else if (section === "users") {
      tasks.push(loadAdminsSection(adminsPage));
    } else if (section === "settings") {
      if (settingsSubsection === "security" || settingsSubsection === "general") {
        tasks.push(loadSecuritySettings());
      } else if (settingsSubsection === "moderation") {
        tasks.push(loadBlockWordSettings());
      } else if (settingsSubsection === "product-assistant") {
        tasks.push(loadProductQuickQuestions(), loadProductCategories(), loadProductOverrides());
      } else if (settingsSubsection === "retrieval-profile") {
        tasks.push(loadRetrievalProfile());
      }
    }
    if (tasks.length === 0) return;
    let cancelled = false;
    setSectionLoading(true);
    Promise.allSettled(tasks).then(() => {
      if (!cancelled) setSectionLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    effectiveTenantId,
    section,
    usersSubsection,
    settingsSubsection,
    chatFilter,
    chatsPage,
    adminsPage,
    loadOverviewSection,
    loadChatsSection,
    loadJobsSection,
    loadAdminsSection,
    loadProductQuickQuestions,
    loadProductCategories,
    loadProductOverrides,
    loadSecuritySettings,
    loadBlockWordSettings,
    loadRetrievalProfile,
  ]);

  const loadSession = async (id) => {
    setSelected(id);
    setMessages([]);
    setSessionLoading(true);
    const requestSeq = ++sessionRequestRef.current;
    try {
      const { data } = await client.get(`/api/admin/chats/${id}`);
      if (requestSeq === sessionRequestRef.current) {
        setMessages(data);
      }
    } finally {
      if (requestSeq === sessionRequestRef.current) setSessionLoading(false);
    }
  };

  const reindex = async () => {
    await client.post("/api/reindex", role === "superadmin" ? { tenant_id: sourceTenantId || undefined } : { tenant_id: tenantId });
    await loadJobsSection();
  };

  const createAdmin = async () => {
    try {
      const payload = {
        email: adminEmail,
        password: adminPassword,
        role: newUserRole,
      };
      if (adminTenantMode === "new") {
        payload.new_tenant_name = newTenantName.trim() || undefined;
      } else {
        payload.tenant_id = adminTenantId || undefined;
      }
      await client.post("/api/admin/users", {
        ...payload,
      });
      setSuccess("User created successfully");
      setCreateOpen(false);
      setAdminEmail("");
      setAdminPassword("");
      setAdminTenantId("");
      setAdminTenantMode("existing");
      setNewTenantName("");
      setNewUserRole(role === "admin" ? "manager" : "admin");
      await loadAdminsSection(adminsPage);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create user");
    }
  };

  const updateUserStatus = async (userId, isActive) => {
    try {
      await client.post(`/api/admin/users/${userId}/status`, { is_active: isActive });
      setSuccess(`User ${isActive ? "activated" : "deactivated"} successfully`);
      await loadAdminsSection(adminsPage);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update user status");
    }
  };

  const openReset = (userId) => {
    setResetUserId(userId);
    setNewPassword("");
    setResetOpen(true);
  };

  const resetPassword = async () => {
    try {
      await client.post(`/api/admin/users/${resetUserId}/reset-password`, { new_password: newPassword });
      setSuccess("Password reset successfully");
      setResetOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to reset password");
    }
  };

  const openManageUserTenants = async (user) => {
    try {
      const { data } = await client.get(`/api/admin/users/${user.id}/tenants`);
      const ids = (data?.items || []).map((i) => i.tenant_id);
      setManageTenantUser(user);
      setManageTenantIds(ids);
      setManageTenantsOpen(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load user tenant associations");
    }
  };

  const saveManageUserTenants = async () => {
    if (!manageTenantUser) return;
    setManageTenantSaving(true);
    try {
      await client.put(`/api/admin/users/${manageTenantUser.id}/tenants`, { tenant_ids: manageTenantIds });
      setSuccess("User tenant associations updated");
      setManageTenantsOpen(false);
      setManageTenantUser(null);
      setManageTenantIds([]);
      await loadAdminsSection(adminsPage);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update user tenant associations");
    } finally {
      setManageTenantSaving(false);
    }
  };

  const createStandaloneTenant = async () => {
    const name = newStandaloneTenantName.trim();
    if (!name) return;
    try {
      await client.post("/api/admin/tenants", { name });
      setSuccess("Tenant created");
      setCreateTenantOpen(false);
      setNewStandaloneTenantName("");
      await loadTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create tenant");
    }
  };

  const applySourceDbTypeChange = (nextType) => {
    setSourceDbType(nextType);
    const nextDefaults = applySourceDefaultsForDbType(nextType, {
      tablePrefix: sourceTablePrefix,
      urlTable: sourceUrlTable,
    });
    setSourceTablePrefix(nextDefaults.tablePrefix);
    setSourceUrlTable(nextDefaults.urlTable);
    setSourceMode((prev) => normalizeSourceModeForDbType(nextType, prev));
  };

  const saveSourceConfig = async () => {
    const effectiveMode = normalizeSourceModeForDbType(sourceDbType, sourceMode);
    try {
      await client.patch(`/api/admin/tenants/${sourceTenantId}/source-config`, {
        source_db_url: sourceDbUrl || null,
        source_db_type: sourceDbType || null,
        source_table_prefix: sourceTablePrefix.trim(),
        source_url_table: sourceUrlTable.trim() || null,
        source_mode: effectiveMode || null,
        source_static_urls_json: sourceStaticUrlsJson || null,
        source_domain_aliases: sourceDomainAliases || null,
        source_canonical_base_url: sourceCanonicalBaseUrl || null,
      });
      setSourceMode(effectiveMode);
      setSuccess("Tenant source settings updated");
      await loadTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update source settings");
    }
  };

  const saveBrandingConfig = async () => {
    try {
      await client.patch(`/api/admin/tenants/${sourceTenantId}/branding`, {
        brand_name: brandName || null,
        widget_primary_color: widgetPrimaryColor || null,
      });
      setSuccess("Branding updated");
      await loadTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update branding settings");
    }
  };

  const saveSecuritySettings = async () => {
    if (!effectiveTenantId) return;
    try {
      const quotaPayload = {
        quota_reached_message: quotaReachedMessage,
        ai_enabled: aiEnabled,
      };
      if (role === "superadmin") {
        quotaPayload.monthly_message_limit = Number(monthlyMessageLimit || 15000);
      }
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/quota`, quotaPayload);
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/branding`, {
        cors_allowed_origins: corsAllowedOrigins || null,
        widget_powered_by_text: poweredByText,
      });
      setSuccess("Security settings updated");
      await loadSecuritySettings();
      await loadTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update security settings");
    }
  };

  const addBlockedIp = async () => {
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/blocked-ips`, {
        ip_address: newBlockedIp,
        reason: newBlockedIpReason,
      });
      setNewBlockedIp("");
      setNewBlockedIpReason("");
      await loadSecuritySettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add blocked IP");
    }
  };

  const removeBlockedIp = async (id) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/blocked-ips/${id}`);
      await loadSecuritySettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to remove blocked IP");
    }
  };

  const addBlockedCountry = async () => {
    if (!newBlockedCountry) {
      setError("Select a country to block");
      return;
    }
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/blocked-countries`, {
        country_code: newBlockedCountry,
        reason: newBlockedCountryReason,
      });
      setNewBlockedCountry("");
      setNewBlockedCountryReason("");
      await loadSecuritySettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add blocked country");
    }
  };

  const removeBlockedCountry = async (id) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/blocked-countries/${id}`);
      await loadSecuritySettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to remove blocked country");
    }
  };

  const createBlockWordCategory = async () => {
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/block-word-categories`, {
        name: newCategoryName,
        match_mode: newCategoryMode,
        response_message: newCategoryResponse,
      });
      setNewCategoryName("");
      setNewCategoryMode("exact");
      setNewCategoryResponse("");
      await loadBlockWordSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create category");
    }
  };

  const deleteBlockWordCategory = async (categoryId) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/block-word-categories/${categoryId}`);
      await loadBlockWordSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete category");
    }
  };

  const addWordToCategory = async (categoryId) => {
    const word = (newWordByCategory[categoryId] || "").trim();
    if (!word) return;
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/block-word-categories/${categoryId}/words`, { word });
      setNewWordByCategory((prev) => ({ ...prev, [categoryId]: "" }));
      await loadBlockWordSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add word");
    }
  };

  const deleteWordFromCategory = async (categoryId, wordId) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/block-word-categories/${categoryId}/words/${wordId}`);
      await loadBlockWordSettings();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete word");
    }
  };

  const saveProductAssistantSettings = async () => {
    try {
      await client.patch(`/api/admin/tenants/${sourceTenantId}/product-assistant`, {
        product_widget_enabled: !!productWidgetEnabled,
        product_widget_system_prompt: productWidgetSystemPrompt || null,
        product_widget_offtopic_message: productWidgetOfftopicMessage || null,
        product_questions_feed_url: productQuestionsFeedUrl.trim(),
        product_widget_custom_css: productWidgetCustomCss,
      });
      setSuccess("Product assistant settings saved");
      await loadTenants();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save product assistant settings");
    }
  };

  const syncQuestionsFeedNow = async () => {
    if (!effectiveTenantId) return;
    setQuestionsFeedSyncBusy(true);
    try {
      // Save the URL first so "set URL → Sync now" works in one go.
      await client.patch(`/api/admin/tenants/${sourceTenantId}/product-assistant`, {
        product_questions_feed_url: productQuestionsFeedUrl.trim(),
      });
      const { data } = await client.post(`/api/admin/tenants/${effectiveTenantId}/product-questions-feed/sync`);
      setProductQuickQuestions(data.quick_questions || []);
      setSuccess(`Questions feed synced: ${data.synced} questions in ${data.categories} categories`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to sync questions feed");
    } finally {
      setQuestionsFeedSyncBusy(false);
    }
  };

  const newPqQuestionLines = newPqQuestionText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const createProductQuickQuestion = async () => {
    if (!newPqQuestionLines.length) return;
    try {
      const { data } = await client.post(`/api/admin/tenants/${effectiveTenantId}/product-quick-questions/bulk`, {
        category_matches: newPqCategories.length ? newPqCategories : [null],
        questions: newPqQuestionLines,
        priority: Number(newPqPriority || 0),
        enabled: true,
      });
      setNewPqQuestionText("");
      setNewPqPriority("0");
      const dup = data?.skipped_duplicates ? ` (${data.skipped_duplicates} duplicate${data.skipped_duplicates > 1 ? "s" : ""} skipped)` : "";
      setSuccess(`${data?.created ?? 0} quick question${data?.created === 1 ? "" : "s"} added${dup}`);
      await Promise.all([loadProductQuickQuestions(), loadProductCategories()]);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add quick questions");
    }
  };

  const toggleNewPqCategory = (name) => {
    setNewPqCategories((prev) => (prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]));
  };

  const deleteProductQuickQuestion = async (id) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/product-quick-questions/${id}`);
      await Promise.all([loadProductQuickQuestions(), loadProductCategories()]);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete quick question");
    }
  };

  const setProductQuickQuestionEnabled = async (row, enabled) => {
    try {
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/product-quick-questions/${row.id}`, { enabled });
      await loadProductQuickQuestions();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update quick question");
    }
  };

  const updateProductQuickQuestionDraft = (id, patch) => {
    setProductQuickQuestionDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const resetProductQuickQuestionDraft = (row) => {
    setProductQuickQuestionDrafts((prev) => ({ ...prev, [row.id]: { ...row } }));
  };

  const saveProductQuickQuestionDraft = async (row) => {
    const draft = productQuickQuestionDrafts[row.id] || row;
    try {
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/product-quick-questions/${row.id}`, {
        category_match: (draft.category_match || "").trim() || null,
        question_text: (draft.question_text || "").trim(),
        priority: Number(draft.priority || 0),
        enabled: !!draft.enabled,
      });
      setSuccess("Quick question updated");
      await Promise.all([loadProductQuickQuestions(), loadProductCategories()]);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save quick question");
    }
  };

  const isProductQuickQuestionDirty = (row) => {
    const draft = productQuickQuestionDrafts[row.id];
    if (!draft) return false;
    const toNorm = (v) => String(v ?? "").trim();
    return (
      toNorm(draft.category_match) !== toNorm(row.category_match) ||
      toNorm(draft.question_text) !== toNorm(row.question_text) ||
      toNorm(draft.priority) !== toNorm(row.priority) ||
      Boolean(draft.enabled) !== Boolean(row.enabled)
    );
  };

  const createProductOverride = async () => {
    if (!newPoProductKey.trim()) return;
    try {
      await client.post(`/api/admin/tenants/${effectiveTenantId}/product-overrides`, {
        product_key: newPoProductKey.trim(),
        product_label: newPoProductLabel.trim() || null,
        extra_context: newPoExtraContext.trim() || null,
      });
      setNewPoProductKey("");
      setNewPoProductLabel("");
      setNewPoExtraContext("");
      setSuccess("Product override added");
      await loadProductOverrides();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add product override");
    }
  };

  const deleteProductOverride = async (id) => {
    try {
      await client.delete(`/api/admin/tenants/${effectiveTenantId}/product-overrides/${id}`);
      await loadProductOverrides();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete product override");
    }
  };

  const updateProductOverrideDraft = (id, patch) => {
    setProductOverrideDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const resetProductOverrideDraft = (row) => {
    setProductOverrideDrafts((prev) => ({ ...prev, [row.id]: { ...row } }));
  };

  const saveProductOverrideDraft = async (row) => {
    const draft = productOverrideDrafts[row.id] || row;
    try {
      await client.patch(`/api/admin/tenants/${effectiveTenantId}/product-overrides/${row.id}`, {
        product_key: (draft.product_key || "").trim(),
        product_label: (draft.product_label || "").trim() || null,
        extra_context: (draft.extra_context || "").trim() || null,
      });
      setSuccess("Product override updated");
      await loadProductOverrides();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save product override");
    }
  };

  const isProductOverrideDirty = (row) => {
    const draft = productOverrideDrafts[row.id];
    if (!draft) return false;
    const toNorm = (v) => String(v ?? "").trim();
    return (
      toNorm(draft.product_key) !== toNorm(row.product_key) ||
      toNorm(draft.product_label) !== toNorm(row.product_label) ||
      toNorm(draft.extra_context) !== toNorm(row.extra_context)
    );
  };

  const exportTranscript = () => {
    if (!selected || !messages.length) return;
    const lines = messages.map((m) => `[${m.created_at}] ${m.sender_type.toUpperCase()}: ${m.content}`);
    const blob = new Blob([lines.join("\n\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `session-${selected}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Box
          component="aside"
          sx={{
            width: { xs: "100%", md: 256 },
            flexShrink: 0,
            bgcolor: "#0c3835",
            color: "#d7ece8",
            p: 2,
            position: { md: "sticky" },
            top: { md: 64 },
            alignSelf: "flex-start",
            height: { md: "calc(100vh - 64px)" },
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
              <Typography variant="overline" sx={{ color: "#5f8f88", fontWeight: 700, letterSpacing: "0.14em", px: 1, pb: 0.5 }}>
                Menu
              </Typography>
              <Stack spacing={0.5}>
                {[
                  { key: "overview", label: "Overview", to: "overview", Icon: QueryStatsRoundedIcon },
                  { key: "chats", label: "Chats", to: "chats", Icon: ChatBubbleOutlineRoundedIcon },
                  { key: "jobs", label: "Jobs", to: "jobs", Icon: BoltOutlinedIcon },
                  { key: "users", label: "Users", to: "users/admins", Icon: PeopleAltOutlinedIcon },
                  { key: "settings", label: "Settings", to: "settings/general", Icon: SettingsOutlinedIcon },
                ].map(({ key, label, to, Icon }) => {
                  const active = section === key;
                  return (
                    <Button
                      key={key}
                      onClick={() => navigateSection(to)}
                      startIcon={<Icon fontSize="small" />}
                      endIcon={
                        key === "settings" ? (
                          <ExpandMoreRoundedIcon
                            fontSize="small"
                            sx={{ transition: "transform .2s", transform: active ? "rotate(180deg)" : "none" }}
                          />
                        ) : null
                      }
                      sx={{
                        justifyContent: "flex-start",
                        width: "100%",
                        px: 1.5,
                        borderRadius: 2,
                        textAlign: "left",
                        fontWeight: 600,
                        color: active ? "#ffffff" : "#9fc4be",
                        bgcolor: active ? "rgba(45,212,191,0.16)" : "transparent",
                        "& .MuiButton-startIcon": { color: active ? "#2dd4bf" : "#6ba49d" },
                        "&:hover": { bgcolor: active ? "rgba(45,212,191,0.22)" : "rgba(255,255,255,0.06)" },
                      }}
                    >
                      <Box component="span" sx={{ flexGrow: 1 }}>{label}</Box>
                    </Button>
                  );
                })}
                {section === "settings" && (
                  <Stack spacing={0.25} sx={{ ml: 2.75, pl: 1.25, borderLeft: "1px solid rgba(255,255,255,0.14)" }}>
                    {[
                      ["general", "General"],
                      ["security", "Security"],
                      ["branding", "Branding"],
                      ["moderation", "Moderation"],
                      ["product-assistant", "Product Assistant"],
                      ["db-settings", "DB Settings"],
                      ...(role === "superadmin" ? [["retrieval-profile", "Retrieval profile"]] : []),
                    ].map(([key, label]) => {
                      const active = settingsSubsection === key;
                      return (
                        <Button
                          key={key}
                          size="small"
                          onClick={() => navigateSettingsSubsection(key)}
                          sx={{
                            justifyContent: "flex-start",
                            width: "100%",
                            px: 1.25,
                            borderRadius: 1.5,
                            textAlign: "left",
                            fontWeight: active ? 700 : 500,
                            color: active ? "#2dd4bf" : "#9fc4be",
                            bgcolor: active ? "rgba(45,212,191,0.12)" : "transparent",
                            "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
              <Box sx={{ mt: "auto", pt: 2 }}>
                <Divider sx={{ mb: 1.5, borderColor: "rgba(255,255,255,0.12)" }} />
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      fontSize: 16,
                      fontWeight: 700,
                      background: "linear-gradient(135deg,#14b8a6,#0f766e)",
                      color: "#fff",
                    }}
                  >
                    {(userEmail || "?").trim().charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      variant="subtitle2"
                      noWrap
                      title={userEmail || ""}
                      sx={{ fontWeight: 700, lineHeight: 1.2, color: "#ffffff" }}
                    >
                      {userEmail ? userEmail.split("@")[0] : "Signed in"}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ display: "block", color: "#9fc4be" }} title={userEmail || ""}>
                      {userEmail || ""}
                    </Typography>
                  </Box>
                </Stack>
                {role && (
                  <Chip
                    size="small"
                    label={String(role).replace(/_/g, " ")}
                    sx={{ mt: 1, ml: 0.5, textTransform: "capitalize", fontWeight: 600, bgcolor: "rgba(255,255,255,0.10)", color: "#d7ece8" }}
                  />
                )}
                {onLogout && (
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<LogoutRoundedIcon fontSize="small" />}
                    onClick={onLogout}
                    sx={{
                      mt: 1.5,
                      justifyContent: "flex-start",
                      color: "#d7ece8",
                      borderColor: "rgba(255,255,255,0.25)",
                      "&:hover": { borderColor: "#2dd4bf", bgcolor: "rgba(45,212,191,0.08)" },
                    }}
                  >
                    Sign out
                  </Button>
                )}
              </Box>
        </Box>
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4" fontWeight={700}>Assistant Studio</Typography>
          <Typography color="text.secondary">{role === "superadmin" ? "All workspaces" : "Your workspace"}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {canSelectTenant && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Tenant</InputLabel>
              <Select
                label="Tenant"
                value={sourceTenantId}
                onChange={(e) => persistSourceTenantId(e.target.value)}
              >
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                    {role === "superadmin" ? ` (${t.id})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button variant="outlined" onClick={reindex}>Trigger Reindex</Button>
          {role === "superadmin" && (
            <Button variant="outlined" onClick={() => setCreateTenantOpen(true)}>
              Create Tenant
            </Button>
          )}
          {(role === "superadmin" || role === "admin") && (
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              {role === "superadmin" ? "Add Admin/Manager" : "Add Manager"}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Section-fetch progress: reserve the 4px so content doesn't jump. */}
      <Box sx={{ height: 4, mb: 2, borderRadius: 2, overflow: "hidden" }}>
        {sectionLoading && <LinearProgress sx={{ height: 4 }} />}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{formatApiError(error)}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {section === "overview" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          {/* Product-assistant metrics only. Likes/dislikes and session-rating
              tiles from the parent platform were dropped: this widget has no
              feedback or rating UI, so those would read 0 forever. */}
          {[
            { label: "Conversations", value: overview?.total_chats || 0, Icon: ChatBubbleOutlineRoundedIcon, color: "#0f766e", bg: "#ccfbf1" },
            { label: "Unique Visitors", value: overview?.unique_visitors || 0, Icon: PeopleAltOutlinedIcon, color: "#0ea5e9", bg: "#e0f2fe" },
            { label: "Embeddings Tokens", value: overview?.embedding_token_usage || 0, Icon: AutoAwesomeOutlinedIcon, color: "#7c3aed", bg: "#f3e8ff" },
            { label: "Chat Tokens", value: overview?.chat_token_usage || 0, Icon: BoltOutlinedIcon, color: "#d97706", bg: "#fef3c7" },
          ].map((m) => {
            const Icon = m.Icon;
            const display = typeof m.value === "number" && !Number.isInteger(m.value)
              ? m.value
              : Number(m.value).toLocaleString();
            return (
              <Card key={m.label}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                    <Typography
                      variant="caption"
                      sx={{ textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, color: "text.secondary", mt: 0.5 }}
                    >
                      {m.label}
                    </Typography>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: m.bg,
                        color: m.color,
                        flexShrink: 0,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                  </Stack>
                  <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                    {display}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {section === "chats" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(420px, 1fr) minmax(420px, 1fr)" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Card sx={{ minWidth: 0 }}>
            <CardContent>
              <Stack direction="row" spacing={1} mb={1} alignItems="center">
                <Typography variant="h6">Conversations</Typography>
                <Chip size="small" label={`Embeddings Tokens ${usageSummary?.embedding_total_tokens || 0}`} />
              </Stack>
              <TextField
                size="small"
                label="Search chats"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setChatFilter(e.currentTarget.value);
                    setChatsPage(1);
                    loadChatsSection(e.currentTarget.value, 1);
                  }
                }}
                fullWidth
                sx={{ mb: 1 }}
              />
              <List dense sx={{ maxHeight: 520, overflowY: "auto" }}>
                {sessions.map((s) => (
                  <ListItemButton key={s.id} selected={selected === s.id} onClick={() => loadSession(s.id)}>
                    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ width: "100%", minWidth: 0 }}>
                      <ListItemText
                        sx={{ flex: 1, minWidth: 0 }}
                        primary={s.title || s.id}
                        secondary={`${s.visitor_id || "anonymous"} | ${s.last_message_at}`}
                        secondaryTypographyProps={{ sx: { wordBreak: "break-word" } }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, pt: 0.25 }}>
                        {Number(s.message_count ?? 0)} msgs
                      </Typography>
                    </Stack>
                  </ListItemButton>
                ))}
              </List>
              <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
                <Pagination
                  count={Math.max(1, Math.ceil(chatsTotal / PAGE_SIZE))}
                  page={chatsPage}
                  onChange={(_, value) => setChatsPage(value)}
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 0 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Conversation</Typography>
                <Button size="small" onClick={exportTranscript} disabled={!selected}>Export</Button>
              </Stack>
              {selected && sessionLoading ? (
                <Stack alignItems="center" py={5}>
                  <CircularProgress size={28} />
                </Stack>
              ) : selected ? (
                <Stack spacing={1.5} sx={{ maxHeight: 560, overflowY: "auto" }}>
                  {messages.map((m) => (
                    <Box key={m.id} sx={{ p: 1.5, borderRadius: 1, bgcolor: m.sender_type === "assistant" ? "grey.100" : "primary.50" }}>
                      <Typography variant="caption" color="text.secondary">{m.sender_type}</Typography>
                      <Typography sx={{ wordBreak: "break-word" }}>{m.content}</Typography>
                      {m.sender_type === "assistant" && (
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                          <Chip size="small" label={`Prompt ${m?.token_usage?.prompt_tokens || 0}`} />
                          <Chip size="small" label={`Completion ${m?.token_usage?.completion_tokens || 0}`} />
                          <Chip size="small" label={`Total ${m?.token_usage?.total_tokens || 0}`} />
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">Select a chat session.</Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {section === "jobs" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Reindex Jobs</Typography>
            <Stack spacing={1}>
              {jobs.map((j) => (
                <Card key={j.id} variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Typography sx={{ minWidth: 320 }} fontWeight={600}>{j.id}</Typography>
                      {j.tenant_name && <Chip size="small" label={j.tenant_name} />}
                      <Chip size="small" label={j.scope} />
                      <Chip size="small" color={j.status === "completed" ? "success" : j.status === "failed" ? "error" : "default"} label={j.status} />
                    </Stack>
                    {(() => {
                      const prog = reindexJobProgress(j);
                      if (j.status === "completed" || j.status === "failed") return null;
                      // Discovery phase: the catalog is still being fetched from
                      // the store database. When the adapter reports fetch counts
                      // show a real percentage; otherwise an active indeterminate bar.
                      const meta = j?.meta?.progress || {};
                      const discovering = !prog || prog.total <= 0 || meta.stage === "discovering";
                      if (discovering) {
                        const dTotal = Number(meta.discovery_total || 0);
                        const dDone = Number(meta.discovery_done || 0);
                        if (dTotal > 0) {
                          const fetchDone = dDone >= dTotal;
                          const actualPct = Math.min(100, (dDone / dTotal) * 100);
                          const dPct = fetchDone
                            ? 100
                            : Math.min(99, Math.max(actualPct, smoothDiscoveryPct[j.id] || 0));
                          return (
                            <Box sx={{ mb: 1 }}>
                              <LinearProgress
                                variant={fetchDone ? "indeterminate" : "determinate"}
                                value={dPct}
                                sx={{ height: 8, borderRadius: 10, mb: 0.5 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {fetchDone
                                  ? `Catalog fetched (${dTotal.toLocaleString()} products). Loading variants & preparing records… this can take a while on slow store databases.`
                                  : `Discovering catalog: ${Math.floor(dPct)}% (${dDone.toLocaleString()}/${dTotal.toLocaleString()} products fetched)`}
                              </Typography>
                            </Box>
                          );
                        }
                        return (
                          <Box sx={{ mb: 1 }}>
                            <LinearProgress sx={{ height: 8, borderRadius: 10, mb: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                              Discovering catalog from the store database… this phase can take a long
                              time for large or remote catalogs.
                            </Typography>
                          </Box>
                        );
                      }
                      return (
                      <Box sx={{ mb: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={prog.percentage}
                          sx={{ height: 8, borderRadius: 10, mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {prog.processed}/{prog.total} processed ({prog.percentage.toFixed(1)}%)
                          {prog.remaining > 0 ? ` · ${prog.remaining} remaining` : ""}
                        </Typography>
                      </Box>
                      );
                    })()}
                    <Typography variant="caption" color="text.secondary" display="block">
                      Started: {j.started_at || "N/A"} {j.finished_at ? `| Finished: ${j.finished_at}` : ""}
                    </Typography>
                    {!!j.error && (
                      <Typography variant="caption" color="error.main" display="block">
                        Error: {j.error}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {section === "users" && usersSubsection === "admins" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Admins</Typography>
            <Stack spacing={1}>
              {users.map((u) => (
                <Stack key={u.id} direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ minWidth: 280 }}>{u.email}</Typography>
                  <Chip size="small" label={u.role} color={u.role === "superadmin" ? "secondary" : "primary"} />
                  <Chip size="small" label={u.is_active ? "active" : "inactive"} />
                  {u.role !== "superadmin" && (
                    <>
                      <Button size="small" onClick={() => updateUserStatus(u.id, !u.is_active)}>
                        {u.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button size="small" onClick={() => openReset(u.id)}>Reset Password</Button>
                      {(role === "superadmin" || (role === "admin" && u.role === "manager")) && (
                        <Button size="small" onClick={() => openManageUserTenants(u)}>
                          Manage Tenants
                        </Button>
                      )}
                    </>
                  )}
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
              <Pagination
                count={Math.max(1, Math.ceil(adminsTotal / PAGE_SIZE))}
                page={adminsPage}
                onChange={(_, value) => setAdminsPage(value)}
                size="small"
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {section === "settings" && settingsSubsection === "general" && (
        <Stack spacing={2}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: aiEnabled ? "success.main" : "error.main",
            borderLeft: "6px solid",
            borderLeftColor: aiEnabled ? "success.main" : "error.main",
            bgcolor: aiEnabled ? "rgba(46,125,50,0.06)" : "rgba(211,47,47,0.06)",
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="h6">AI Assistant</Typography>
                  <Chip
                    size="small"
                    label={aiEnabled ? "ACTIVE" : "DISABLED"}
                    color={aiEnabled ? "success" : "error"}
                    variant={aiEnabled ? "filled" : "outlined"}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Master switch for this tenant. When off, all assistants are fully disabled — nothing loads on the customer's site and all AI requests are refused.
                </Typography>
              </Box>
              <Switch
                checked={aiEnabled}
                onChange={(e) => setAiEnabled(e.target.checked)}
                sx={{ transform: "scale(1.3)", mr: 1 }}
              />
            </Stack>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Widget Footer</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Text shown at the bottom of the chat widget (e.g. “Powered by Company Name”). Leave it empty to hide the footer.
            </Typography>
            <Stack spacing={2} sx={{ maxWidth: 480 }}>
              <TextField
                label="Footer text"
                placeholder="Powered by Company Name"
                value={poweredByText}
                onChange={(e) => setPoweredByText(e.target.value)}
                fullWidth
                helperText="Leave empty to hide the footer in the widget."
              />
              <Box>
                <Button variant="contained" onClick={saveSecuritySettings} disabled={!effectiveTenantId}>
                  Save settings
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        </Stack>
      )}

      {section === "settings" && settingsSubsection === "security" && (
        <Stack spacing={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Tenant Security and Quota Controls</Typography>
            <Stack spacing={2}>
              <TextField
                label="Monthly Visitor Message Limit"
                type="number"
                value={monthlyMessageLimit}
                onChange={(e) => setMonthlyMessageLimit(e.target.value)}
                disabled={role !== "superadmin"}
                sx={{ maxWidth: 360 }}
              />
              <TextField
                label="Quota Reached Message"
                value={quotaReachedMessage}
                onChange={(e) => setQuotaReachedMessage(e.target.value)}
                fullWidth
              />
              <TextField
                label="CORS Allowed Origins (comma-separated)"
                value={corsAllowedOrigins}
                onChange={(e) => setCorsAllowedOrigins(e.target.value)}
                fullWidth
              />
              <Button variant="contained" onClick={saveSecuritySettings} disabled={!effectiveTenantId}>
                Save security settings
              </Button>

              <Typography variant="subtitle1">Blocked IPs</Typography>
              <Stack direction="row" spacing={1}>
                <TextField label="IP address" value={newBlockedIp} onChange={(e) => setNewBlockedIp(e.target.value)} />
                <TextField label="Reason" value={newBlockedIpReason} onChange={(e) => setNewBlockedIpReason(e.target.value)} />
                <Button variant="outlined" onClick={addBlockedIp}>Add IP</Button>
              </Stack>
              <Stack spacing={1}>
                {blockedIps.map((item) => (
                  <Stack key={item.id} direction="row" spacing={1} alignItems="center">
                    <Chip label={item.ip_address} />
                    {item.reason && <Chip variant="outlined" label={item.reason} />}
                    <Button size="small" color="error" onClick={() => removeBlockedIp(item.id)}>Remove</Button>
                  </Stack>
                ))}
              </Stack>

              <Typography variant="subtitle1">Blocked Countries (GeoIP ISO alpha-2)</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <FormControl sx={{ minWidth: 280 }}>
                  <InputLabel id="blocked-country-select-label">Country</InputLabel>
                  <Select
                    labelId="blocked-country-select-label"
                    label="Country"
                    value={newBlockedCountry}
                    onChange={(e) => setNewBlockedCountry(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select…</em>
                    </MenuItem>
                    {countryReference.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="Reason" value={newBlockedCountryReason} onChange={(e) => setNewBlockedCountryReason(e.target.value)} sx={{ minWidth: 200 }} />
                <Button variant="outlined" onClick={addBlockedCountry} disabled={!effectiveTenantId}>
                  Add Country
                </Button>
              </Stack>
              <Stack spacing={1}>
                {blockedCountries.map((item) => (
                  <Stack key={item.id} direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={
                        countryLabelByCode[item.country_code]
                          ? `${countryLabelByCode[item.country_code]} (${item.country_code})`
                          : item.country_code
                      }
                    />
                    {item.reason && <Chip variant="outlined" label={item.reason} />}
                    <Button size="small" color="error" onClick={() => removeBlockedCountry(item.id)}>Remove</Button>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        </Stack>
      )}

      {section === "settings" && settingsSubsection === "branding" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Branding</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Brand name is used in the assistant's answers and prompt; the primary color tints the
              question pills and input accent on the product widget.
            </Typography>
            <Stack spacing={2} sx={{ maxWidth: 520 }}>
              <TextField label="Brand Name" value={brandName} onChange={(e) => setBrandName(e.target.value)} fullWidth />
              <TextField
                label="Widget Primary Color"
                value={widgetPrimaryColor}
                onChange={(e) => setWidgetPrimaryColor(e.target.value)}
                placeholder="#1a56db"
                helperText="Hex color, e.g. #1a56db. Leave empty for the default blue."
                fullWidth
              />
              <Box>
                <Button variant="contained" onClick={saveBrandingConfig} disabled={!sourceTenantId}>Save Branding</Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {section === "settings" && settingsSubsection === "moderation" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Moderation</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Block word policies stop a message before any answer is generated and reply with the
              category's response message instead. Applies to the product assistant too.
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Match Mode</InputLabel>
                  <Select label="Match Mode" value={newCategoryMode} onChange={(e) => setNewCategoryMode(e.target.value)}>
                    <MenuItem value="exact">Exact</MenuItem>
                    <MenuItem value="substring">Substring</MenuItem>
                    <MenuItem value="regex">Regex</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <TextField
                label="Category response message"
                value={newCategoryResponse}
                onChange={(e) => setNewCategoryResponse(e.target.value)}
                fullWidth
              />
              <Button variant="contained" onClick={createBlockWordCategory} disabled={!newCategoryName.trim() || !newCategoryResponse.trim()}>
                Add Category
              </Button>
              <Stack spacing={1.5}>
                {blockWordCategories.map((category) => (
                  <Card key={category.id} variant="outlined">
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <Chip label={category.name} />
                        <Chip variant="outlined" label={category.match_mode} />
                        <Button size="small" color="error" onClick={() => deleteBlockWordCategory(category.id)}>Delete Category</Button>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" mb={1}>{category.response_message}</Typography>
                      <Stack direction="row" spacing={1} mb={1}>
                        <TextField
                          size="small"
                          label="Add trigger word"
                          value={newWordByCategory[category.id] || ""}
                          onChange={(e) => setNewWordByCategory((prev) => ({ ...prev, [category.id]: e.target.value }))}
                        />
                        <Button size="small" variant="outlined" onClick={() => addWordToCategory(category.id)}>Add Word</Button>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(category.words || []).map((w) => (
                          <Chip
                            key={w.id}
                            label={w.word}
                            onDelete={() => deleteWordFromCategory(category.id, w.id)}
                            sx={{ mb: 1 }}
                          />
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {section === "settings" && settingsSubsection === "product-assistant" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Product Assistant</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              A single-product-scoped chatbot for product pages. It answers only about the one
              product it is embedded on (specs, features, compatibility, warranty, usage, pricing,
              availability) — the product must already be indexed in this tenant's catalog.
            </Typography>
            <Tabs value={productAssistantPanel} onChange={(_, v) => setProductAssistantPanel(v)} sx={{ mb: 2 }}>
              <Tab label="Settings" />
              <Tab label="Quick questions" />
              <Tab label="Product overrides" />
            </Tabs>

            {productAssistantPanel === 0 && (
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={productWidgetEnabled}
                      onChange={(e) => setProductWidgetEnabled(e.target.checked)}
                    />
                  }
                  label="Enable the product-page assistant for this tenant"
                />
                <TextField
                  label="Additional instructions (optional)"
                  helperText="Persona/tone guidance appended to the built-in scope-lock prompt. Do not paste a full prompt template here — just extra guidance (e.g. 'Speak like a knowledgeable guitar tech.')."
                  value={productWidgetSystemPrompt}
                  onChange={(e) => setProductWidgetSystemPrompt(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
                <TextField
                  label="Out-of-scope reply"
                  helperText="Shown when a visitor asks about something outside this product (another product, cart/checkout, general site info). Leave empty to use the default message."
                  value={productWidgetOfftopicMessage}
                  onChange={(e) => setProductWidgetOfftopicMessage(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Custom CSS (advanced)"
                  helperText={
                    'Injected inside the widget iframe. Target the stable class names: .pa-panel, .pa-heading, .pa-chip, ' +
                    '.pa-ask-chip, .pa-conversation, .pa-question, .pa-answer, .pa-input-row, .pa-input, .pa-send, ' +
                    '.pa-powered-by. Example: .pa-chip { background:#fff; color:#334f67; border:1px solid #334f67; border-radius:2px; }'
                  }
                  value={productWidgetCustomCss}
                  onChange={(e) => setProductWidgetCustomCss(e.target.value)}
                  fullWidth
                  multiline
                  minRows={5}
                  InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
                />
                <Box>
                  <Button variant="contained" onClick={saveProductAssistantSettings} disabled={!sourceTenantId}>
                    Save settings
                  </Button>
                </Box>
                <Divider />
                <Typography variant="subtitle2">Embed snippet</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add this <b>once</b> to your site theme (e.g. the global template/footer) — no
                  per-page work needed. It does nothing on non-product pages; on product pages it
                  auto-detects the product from the JSON-LD markup and renders at{" "}
                  <code>mountSelector</code>. The site origin must be listed in CORS Allowed Origins
                  (under Security).
                </Typography>
                <TextField
                  value={embedConfig.snippet}
                  fullWidth
                  multiline
                  minRows={10}
                  InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 13 } }}
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    onClick={regenerateWidgetKey}
                    disabled={!effectiveTenantId || keyBusy}
                  >
                    {keyBusy
                      ? "Working…"
                      : embedConfig.tenantPublicKey
                        ? "Regenerate key"
                        : "Generate key"}
                  </Button>
                  <Button variant="contained" onClick={copyEmbedSnippet} disabled={!selectedTenant}>
                    {embedCopied ? "Copied!" : "Copy snippet"}
                  </Button>
                  {!embedConfig.tenantPublicKey && (
                    <Typography variant="caption" color="error">
                      Snippet uses a placeholder key until you generate one.
                    </Typography>
                  )}
                </Stack>
              </Stack>
            )}

            {productAssistantPanel === 1 && (
              <Stack spacing={2}>
                <Alert severity="info">
                  Shown as tappable chips above the "Ask anything" box. Pick one or more catalog
                  categories to show questions only on those products, or leave none selected to show
                  them on every product. Up to 6 chips are shown per product, category-specific first.
                </Alert>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" mb={1}>Questions feed (auto-sync)</Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Optional: a standalone <code>&lt;category_questions&gt;</code> XML feed URL.
                      It is fetched automatically every hour and replaces the feed-sourced
                      questions below (marked <b>feed</b>) — questions added manually on this page
                      are never touched. Use "Sync now" to pull it immediately.
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                      <TextField
                        label="Questions feed URL"
                        placeholder="https://store.com/media/feed/category-questions-feed.xml"
                        value={productQuestionsFeedUrl}
                        onChange={(e) => setProductQuestionsFeedUrl(e.target.value)}
                        sx={{ flexGrow: 1, minWidth: 320 }}
                      />
                      <Button variant="contained" onClick={syncQuestionsFeedNow} disabled={!effectiveTenantId || questionsFeedSyncBusy || !productQuestionsFeedUrl.trim()}>
                        {questionsFeedSyncBusy ? "Syncing…" : "Sync now"}
                      </Button>
                      <Button variant="outlined" onClick={saveProductAssistantSettings} disabled={!sourceTenantId}>
                        Save URL
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap mb={1}>
                      <Typography variant="subtitle2">
                        Categories in your catalog ({productCategories.length})
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => setNewPqCategories(productCategories.map((c) => c.name))} disabled={!productCategories.length}>
                          Select all categories
                        </Button>
                        <Button size="small" onClick={() => setNewPqCategories([])} disabled={!newPqCategories.length}>
                          All products
                        </Button>
                      </Stack>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      Read from the indexed products. Click a category to target it with the questions below;
                      with none selected the questions apply to <b>all products</b>. Counts show products / questions already set.
                    </Typography>
                    {productCategories.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No products indexed yet — run a sync from DB Settings first.</Typography>
                    ) : (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {productCategories.map((cat) => {
                          const selected = newPqCategories.includes(cat.name);
                          return (
                            <Chip
                              key={cat.name}
                              size="small"
                              clickable
                              color={selected ? "primary" : "default"}
                              variant={selected ? "filled" : "outlined"}
                              onClick={() => toggleNewPqCategory(cat.name)}
                              label={`${cat.name} · ${cat.product_count} / ${cat.question_count}`}
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={productCategories.map((c) => c.name)}
                    value={newPqCategories}
                    onChange={(_e, value) => setNewPqCategories(value.map((v) => String(v).trim()).filter(Boolean))}
                    renderInput={(params) => (
                      <TextField {...params} label="Categories" placeholder={newPqCategories.length ? "" : "All products"} />
                    )}
                    sx={{ flexGrow: 1, minWidth: 320 }}
                  />
                  <TextField
                    label="Priority"
                    value={newPqPriority}
                    onChange={(e) => setNewPqPriority(e.target.value)}
                    sx={{ width: 100 }}
                  />
                </Stack>
                <TextField
                  label="Questions — one per line"
                  value={newPqQuestionText}
                  onChange={(e) => setNewPqQuestionText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder={"Is this in stock?\nWhat is the price?\nWhat is it compatible with?"}
                  helperText={
                    newPqQuestionLines.length
                      ? `${newPqQuestionLines.length} question${newPqQuestionLines.length > 1 ? "s" : ""} × ${newPqCategories.length || "all products"}${newPqCategories.length ? (newPqCategories.length > 1 ? " categories" : " category") : ""} = ${newPqQuestionLines.length * (newPqCategories.length || 1)} chip${newPqQuestionLines.length * (newPqCategories.length || 1) > 1 ? "s" : ""}`
                      : "Add 2–3 short questions shoppers are likely to ask. Duplicates are skipped."
                  }
                />
                <Button variant="contained" onClick={createProductQuickQuestion} disabled={!effectiveTenantId || !newPqQuestionLines.length}>
                  Add {newPqQuestionLines.length > 1 ? `${newPqQuestionLines.length} quick questions` : "quick question"}
                </Button>
                <Stack spacing={1.5}>
                  {productQuickQuestions.map((row) => (
                    <Card key={row.id} variant="outlined">
                      <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={1}>
                          {row.category_match ? <Chip size="small" label={row.category_match} /> : <Chip size="small" variant="outlined" label="all products" />}
                          {row.source === "feed" && <Chip size="small" color="info" variant="outlined" label="feed" />}
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption">Enabled</Typography>
                            <Switch checked={row.enabled} onChange={(e) => setProductQuickQuestionEnabled(row, e.target.checked)} size="small" />
                          </Stack>
                          <Button size="small" color="error" onClick={() => deleteProductQuickQuestion(row.id)}>Delete</Button>
                        </Stack>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={1}>
                          <Autocomplete
                            size="small"
                            freeSolo
                            options={productCategories.map((c) => c.name)}
                            value={(productQuickQuestionDrafts[row.id]?.category_match ?? row.category_match) || ""}
                            onChange={(_e, value) => updateProductQuickQuestionDraft(row.id, { category_match: value || "" })}
                            onInputChange={(_e, value, reason) => {
                              if (reason === "input") updateProductQuickQuestionDraft(row.id, { category_match: value });
                            }}
                            renderInput={(params) => <TextField {...params} label="Category match" placeholder="All products" />}
                            sx={{ minWidth: 240 }}
                          />
                          <TextField
                            size="small"
                            label="Priority"
                            value={String(productQuickQuestionDrafts[row.id]?.priority ?? row.priority ?? 0)}
                            onChange={(e) => updateProductQuickQuestionDraft(row.id, { priority: e.target.value })}
                            sx={{ width: 100 }}
                          />
                        </Stack>
                        <TextField
                          size="small"
                          label="Question text"
                          value={(productQuickQuestionDrafts[row.id]?.question_text ?? row.question_text) || ""}
                          onChange={(e) => updateProductQuickQuestionDraft(row.id, { question_text: e.target.value })}
                          fullWidth
                        />
                        <Stack direction="row" spacing={1} mt={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => saveProductQuickQuestionDraft(row)}
                            disabled={!isProductQuickQuestionDirty(row)}
                          >
                            Save
                          </Button>
                          <Button size="small" variant="text" onClick={() => resetProductQuickQuestionDraft(row)}>
                            Reset
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Stack>
            )}

            {productAssistantPanel === 2 && (
              <Stack spacing={2}>
                <Alert severity="info">
                  Admin-curated grounding data for one specific product — used when a visitor asks
                  something the indexed catalog data doesn't cover (e.g. warranty terms). Match key
                  can be the product's entity_id, SKU, or canonical URL — whichever the embed sends.
                </Alert>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <TextField
                    label="Product key (entity_id / SKU / URL)"
                    value={newPoProductKey}
                    onChange={(e) => setNewPoProductKey(e.target.value)}
                    sx={{ minWidth: 260 }}
                  />
                  <TextField
                    label="Label (optional, for your reference)"
                    value={newPoProductLabel}
                    onChange={(e) => setNewPoProductLabel(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                </Stack>
                <TextField
                  label="Additional notes for the assistant"
                  value={newPoExtraContext}
                  onChange={(e) => setNewPoExtraContext(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="e.g. Covered by a 2-year limited warranty against manufacturing defects."
                />
                <Button variant="contained" onClick={createProductOverride} disabled={!effectiveTenantId || !newPoProductKey.trim()}>
                  Add override
                </Button>
                <Stack spacing={1.5}>
                  {productOverrides.map((row) => (
                    <Card key={row.id} variant="outlined">
                      <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                          <Chip label={row.product_label || row.product_key} />
                          <Button size="small" color="error" onClick={() => deleteProductOverride(row.id)}>Delete</Button>
                        </Stack>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={1}>
                          <TextField
                            size="small"
                            label="Product key"
                            value={(productOverrideDrafts[row.id]?.product_key ?? row.product_key) || ""}
                            onChange={(e) => updateProductOverrideDraft(row.id, { product_key: e.target.value })}
                            sx={{ minWidth: 260 }}
                          />
                          <TextField
                            size="small"
                            label="Label"
                            value={(productOverrideDrafts[row.id]?.product_label ?? row.product_label) || ""}
                            onChange={(e) => updateProductOverrideDraft(row.id, { product_label: e.target.value })}
                            sx={{ minWidth: 220 }}
                          />
                        </Stack>
                        <TextField
                          size="small"
                          label="Additional notes"
                          value={(productOverrideDrafts[row.id]?.extra_context ?? row.extra_context) || ""}
                          onChange={(e) => updateProductOverrideDraft(row.id, { extra_context: e.target.value })}
                          fullWidth
                          multiline
                          minRows={2}
                        />
                        <Stack direction="row" spacing={1} mt={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => saveProductOverrideDraft(row)}
                            disabled={!isProductOverrideDirty(row)}
                          >
                            Save
                          </Button>
                          <Button size="small" variant="text" onClick={() => resetProductOverrideDraft(row)}>
                            Reset
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {section === "settings" && settingsSubsection === "db-settings" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Catalog Source</Typography>
            <Typography color="text.secondary" mb={2}>
              Where the product catalog is read from. <b>Shopify</b> stores are connected
              automatically at install (the plugin sends the token and feed URL over the API) —
              change those only to repair a store. <b>Magento</b> stores are configured here, from a
              product feed XML URL or a remote MySQL DSN. Run "Trigger Reindex" after saving.
            </Typography>
            <Stack spacing={2}>
              {canSelectTenant && (
                <FormControl size="small" sx={{ maxWidth: 420 }}>
                  <InputLabel>Tenant</InputLabel>
                  <Select
                    label="Tenant"
                    value={sourceTenantId}
                    onChange={(e) => persistSourceTenantId(e.target.value)}
                  >
                    {tenants.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl fullWidth>
                <InputLabel id="source-db-type-label">Catalog Platform</InputLabel>
                <Select
                  labelId="source-db-type-label"
                  label="Catalog Platform"
                  value={SOURCE_DB_TYPES.some((t) => t.value === sourceDbType) ? sourceDbType : ""}
                  onChange={(e) => applySourceDbTypeChange(e.target.value)}
                  displayEmpty
                >
                  {!SOURCE_DB_TYPES.some((t) => t.value === sourceDbType) && (
                    <MenuItem value="" disabled>
                      <em>Select a catalog platform…</em>
                    </MenuItem>
                  )}
                  {SOURCE_DB_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={(SOURCE_URL_FIELD[sourceDbType] || SOURCE_URL_FIELD.shopify).label}
                placeholder={(SOURCE_URL_FIELD[sourceDbType] || SOURCE_URL_FIELD.shopify).placeholder}
                value={sourceDbUrl}
                onChange={(e) => setSourceDbUrl(e.target.value)}
                helperText={(SOURCE_URL_FIELD[sourceDbType] || SOURCE_URL_FIELD.shopify).help}
                error={sourceDbType === "magento" && !sourceDbUrl.trim()}
                fullWidth
              />
              {sourceDbType === "magento" && (
                <Alert severity="info" variant="outlined">
                  {!sourceDbUrl.trim()
                    ? "A Magento tenant needs a source: paste the product feed XML URL or the MySQL DSN."
                    : isMagentoFeedUrl(sourceDbUrl)
                      ? "Seeding from the product feed XML. Catalog only — CMS pages are not available without database access; add policy/support pages as static URLs."
                      : "Seeding from the remote Magento database. Catalog and CMS pages are indexed."}
                </Alert>
              )}
              {sourceDbType === "magento" && (
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  <TextField
                    label="Magento store view ID"
                    value={sourceUrlTable}
                    onChange={(e) => setSourceUrlTable(e.target.value)}
                    helperText="Default store view ID (usually 1)"
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    label="Table prefix (optional)"
                    value={sourceTablePrefix}
                    onChange={(e) => setSourceTablePrefix(e.target.value)}
                    helperText="Leave empty for standard Magento 2 table names. Database seeding only."
                    sx={{ minWidth: 260 }}
                  />
                </Stack>
              )}
              <FormControl fullWidth disabled={!SOURCE_DB_TYPES.some((t) => t.value === sourceDbType)}>
                <InputLabel id="source-mode-label">Source Mode</InputLabel>
                <Select
                  labelId="source-mode-label"
                  label="Source Mode"
                  value={normalizeSourceModeForDbType(sourceDbType, sourceMode)}
                  onChange={(e) => setSourceMode(e.target.value)}
                >
                  {(SOURCE_MODE_BY_DB_TYPE[sourceDbType] || SOURCE_MODE_BY_DB_TYPE.shopify).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Canonical Base URL"
                value={sourceCanonicalBaseUrl}
                onChange={(e) => setSourceCanonicalBaseUrl(e.target.value)}
                helperText="Used to normalize equivalent hosts (e.g. prod/staging)"
                fullWidth
              />
              <TextField
                label="Domain Aliases"
                value={sourceDomainAliases}
                onChange={(e) => setSourceDomainAliases(e.target.value)}
                helperText="Comma or newline separated URLs to treat as aliases"
                fullWidth
                multiline
                minRows={2}
              />
              {normalizeSourceModeForDbType(sourceDbType, sourceMode) === "mixed" && (
                <TextField
                  label="Static Source URLs (JSON array or newline list)"
                  value={sourceStaticUrlsJson}
                  onChange={(e) => setSourceStaticUrlsJson(e.target.value)}
                  helperText="Extra pages to index alongside the catalog — one URL per line or a JSON array"
                  fullWidth
                  multiline
                  minRows={4}
                />
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" onClick={saveSourceConfig} disabled={!sourceTenantId}>Save Source Settings</Button>
                {selectedTenant && <Chip label={selectedTenant.name} />}
                {role === "superadmin" && selectedTenant && (
                  <Chip variant="outlined" label={`Tenant ID: ${selectedTenant.id}`} />
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {section === "settings" && settingsSubsection === "retrieval-profile" && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Retrieval profile</Typography>
            <Typography color="text.secondary" mb={2}>
              Built automatically after each catalog reindex (facets, variants, category gazetteer).
              Superadmins can edit gazetteer match flags or replace the full JSON.
            </Typography>
            <Stack spacing={2}>
              {canSelectTenant && (
                <FormControl size="small" sx={{ maxWidth: 420 }}>
                  <InputLabel>Tenant</InputLabel>
                  <Select
                    label="Tenant"
                    value={sourceTenantId}
                    onChange={(e) => persistSourceTenantId(e.target.value)}
                  >
                    {tenants.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="outlined" onClick={loadRetrievalProfile} disabled={!effectiveTenantId || retrievalProfileLoading}>
                  Refresh
                </Button>
                {role === "superadmin" && retrievalProfile?.profile && (
                  <>
                    <Button variant="contained" onClick={openProfileJsonEditor} disabled={profileSaving}>
                      Edit JSON
                    </Button>
                    {Object.keys(gazetteerDrafts).length > 0 && (
                      <Button variant="contained" color="secondary" onClick={saveGazetteerFlags} disabled={profileSaving}>
                        Save gazetteer flags
                      </Button>
                    )}
                  </>
                )}
                {selectedTenant && <Chip label={selectedTenant.name} />}
              </Stack>
              {retrievalProfileLoading && <LinearProgress />}
              {!retrievalProfileLoading && retrievalProfile && !retrievalProfile.profile && (
                <Alert severity="info">
                  No profile — run a catalog reindex for this tenant.
                </Alert>
              )}
              {!retrievalProfileLoading && retrievalProfile?.profile && (() => {
                const profile = retrievalProfile.profile;
                const stats = profile.stats || {};
                const facets = Object.entries(profile.facets || {}).sort(([a], [b]) => a.localeCompare(b));
                const gazetteer = profile.category_strategy?.gazetteer || [];
                const coreFields = profile.core_fields || {};
                const variantAvailability = profile.variant_availability || {};
                return (
                  <>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`Version ${retrievalProfile.retrieval_profile_version ?? profile.profile_version ?? "—"}`} />
                      <Chip label={`${stats.product_count ?? 0} products`} />
                      <Chip label={`${stats.facet_count ?? facets.length} facets`} />
                      {stats.configurable_with_variants != null && (
                        <Chip label={`${stats.configurable_with_variants} with variants`} variant="outlined" />
                      )}
                      {profile.generated_at && <Chip label={`Generated ${profile.generated_at}`} variant="outlined" />}
                    </Stack>
                    {variantAvailability.matrix_qdrant_key && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Variant availability uses Qdrant key &quot;{variantAvailability.matrix_qdrant_key}&quot;
                        {variantAvailability.display_facet_qdrant_path
                          ? ` with display path ${variantAvailability.display_facet_qdrant_path}`
                          : ""}.
                      </Alert>
                    )}
                    {Object.keys(coreFields).length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>Core fields</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {Object.entries(coreFields).map(([key, meta]) => (
                            <Chip
                              key={key}
                              label={`${key}: ${meta.coverage_pct ?? 0}%${meta.indexed ? "" : " (not indexed)"}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Facets</Typography>
                      {facets.length === 0 ? (
                        <Typography color="text.secondary">No facets above coverage threshold.</Typography>
                      ) : (
                        <Stack spacing={2}>
                          {facets.map(([facetId, facet]) => (
                            <Box key={facetId} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography fontWeight={600}>{facetId}</Typography>
                                <Chip size="small" label={`${facet.coverage_pct ?? 0}% coverage`} />
                                <Chip size="small" label={`${facet.product_count ?? 0} products`} variant="outlined" />
                                {facet.indexed && <Chip size="small" label="indexed" color="success" variant="outlined" />}
                              </Stack>
                              {(facet.sample_values || []).length > 0 && (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                  {(facet.sample_values || []).map((v) => (
                                    <Chip key={`${facetId}-${v}`} size="small" label={v} />
                                  ))}
                                </Stack>
                              )}
                              {facet.value_aliases && Object.keys(facet.value_aliases).length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Button
                                    size="small"
                                    onClick={() => setExpandedFacetAliases((prev) => ({ ...prev, [facetId]: !prev[facetId] }))}
                                  >
                                    {expandedFacetAliases[facetId] ? "Hide" : "Show"} value aliases ({Object.keys(facet.value_aliases).length})
                                  </Button>
                                  {expandedFacetAliases[facetId] && (
                                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                      {Object.entries(facet.value_aliases).map(([alias, canonical]) => (
                                        <Typography key={`${facetId}-${alias}`} variant="body2" color="text.secondary">
                                          {alias} → {canonical}
                                        </Typography>
                                      ))}
                                    </Stack>
                                  )}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Category gazetteer</Typography>
                      {gazetteer.length === 0 ? (
                        <Typography color="text.secondary">No category entries.</Typography>
                      ) : (
                        <Stack spacing={1}>
                          {gazetteer.map((entry) => {
                            const draft = gazetteerDrafts[entry.id] || {};
                            const hardFilter = draft.hard_filter ?? Boolean(entry.hard_filter);
                            return (
                            <Box key={entry.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography fontWeight={600}>{entry.id}</Typography>
                                {role === "superadmin" && (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={hardFilter}
                                        onChange={(e) => setGazetteerDrafts((prev) => ({
                                          ...prev,
                                          [entry.id]: { ...prev[entry.id], hard_filter: e.target.checked },
                                        }))}
                                      />
                                    }
                                    label="Hard Qdrant filter"
                                  />
                                )}
                                {role !== "superadmin" && entry.hard_filter && (
                                  <Chip size="small" label="hard filter" color="warning" variant="outlined" />
                                )}
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                Labels: {(entry.labels || []).join(", ") || "—"}
                              </Typography>
                              {(entry.aliases && Object.keys(entry.aliases).length > 0) && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Aliases: {Object.entries(entry.aliases).map(([k, v]) => `${k}→${v}`).join(", ")}
                                </Typography>
                              )}
                            </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </>
                );
              })()}
            </Stack>
          </CardContent>
        </Card>
      )}
      </Box>
      </Box>

      <Dialog open={profileJsonOpen} onClose={() => setProfileJsonOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit retrieval profile (superadmin)</DialogTitle>
        <DialogContent>
          <TextField
            value={profileJsonDraft}
            onChange={(e) => setProfileJsonDraft(e.target.value)}
            multiline
            minRows={16}
            fullWidth
            sx={{ mt: 1, fontFamily: "monospace" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileJsonOpen(false)}>Cancel</Button>
          <Button onClick={saveProfileJson} variant="contained" disabled={profileSaving}>
            Save profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{role === "superadmin" ? "Create Admin/Manager" : "Create Manager"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} fullWidth />
            <TextField label="Password" type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)} disabled={role === "admin"}>
                {role === "superadmin" && <MenuItem value="admin">Admin</MenuItem>}
                <MenuItem value="manager">Manager</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Tenant Option</InputLabel>
              <Select label="Tenant Option" value={adminTenantMode} onChange={(e) => setAdminTenantMode(e.target.value)}>
                <MenuItem value="existing">Use Existing Tenant</MenuItem>
                <MenuItem value="new">Create New Tenant</MenuItem>
              </Select>
            </FormControl>
            {adminTenantMode === "new" ? (
              <TextField
                label="New Tenant Name"
                value={newTenantName}
                onChange={(e) => setNewTenantName(e.target.value)}
                fullWidth
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Tenant</InputLabel>
                <Select label="Tenant" value={adminTenantId} onChange={(e) => setAdminTenantId(e.target.value)}>
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                      {role === "superadmin" ? ` (${t.id})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={createAdmin}
            variant="contained"
            disabled={
              !adminEmail ||
              !adminPassword ||
              !newUserRole ||
              (adminTenantMode === "new" ? !newTenantName.trim() : !adminTenantId)
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manageTenantsOpen} onClose={() => setManageTenantsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage User Tenants</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {manageTenantUser ? `User: ${manageTenantUser.email} (${manageTenantUser.role})` : ""}
            </Typography>
            <FormControl fullWidth>
              {manageTenantUser?.role === "manager" ? (
                <RadioGroup
                  value={manageTenantIds[0] || ""}
                  onChange={(e) => setManageTenantIds(e.target.value ? [e.target.value] : [])}
                >
                  {tenants.map((t) => (
                    <FormControlLabel
                      key={t.id}
                      value={t.id}
                      control={<Radio size="small" />}
                      label={`${t.name}${role === "superadmin" ? ` (${t.id})` : ""}`}
                    />
                  ))}
                </RadioGroup>
              ) : (
                <FormGroup>
                  {tenants.map((t) => (
                    <FormControlLabel
                      key={t.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={manageTenantIds.includes(t.id)}
                          onChange={(e) => {
                            setManageTenantIds((prev) =>
                              e.target.checked ? [...new Set([...prev, t.id])] : prev.filter((id) => id !== t.id)
                            );
                          }}
                        />
                      }
                      label={`${t.name}${role === "superadmin" ? ` (${t.id})` : ""}`}
                    />
                  ))}
                </FormGroup>
              )}
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Managers can only have one tenant. Admins can have multiple.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageTenantsOpen(false)} disabled={manageTenantSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveManageUserTenants}
            disabled={
              !manageTenantUser ||
              manageTenantSaving ||
              (manageTenantUser?.role === "manager" ? manageTenantIds.length !== 1 : false)
            }
          >
            Save Associations
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createTenantOpen} onClose={() => setCreateTenantOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Tenant</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            label="Tenant name"
            value={newStandaloneTenantName}
            onChange={(e) => setNewStandaloneTenantName(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTenantOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createStandaloneTenant} disabled={!newStandaloneTenantName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset User Password</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            type="password"
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={resetPassword} disabled={!newPassword}>Reset</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
